import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { SkillChip } from "@/components/prooftree/SkillChip"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { Textarea, TextareaInput } from "@/components/ui/textarea"
import { VStack } from "@/components/ui/vstack"
import { screenCopy } from "@/constants/Brand"
import { compactFilterChipStyle, filterChipStyle } from "@/constants/ChipStyles"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  ActiveGoal,
  activeGoals,
  allSkills,
  CoachRecommendation,
  ContactSkillTrend,
  createTaskFromCoachAction,
  getCoachAction,
  getCoachRecommendations,
  getContactTrendForTask,
  getDomain,
  getLocalDateKey,
  getSkill,
  getSkillDomainColor,
  getSkills,
  getTodayGrowthSummary,
  GrowthTask,
  NetworkContact,
  tasks,
  Visibility,
} from "@/data/prooftree"
import {
  completeEqTask,
  createEqGoal,
  createEqJournalEntry,
  createEqTask,
  listEqGoals,
  listEqTasks,
  recordEqContactInteraction,
} from "@/services/eqParityBackend"
import { isSupabaseUuid } from "@/services/prooftreeBackend"
import { router, useLocalSearchParams } from "expo-router"
import {
  BookOpenText,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Filter,
  Flag,
  Globe2,
  Link2,
  ListTodo,
  Lock,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UsersRound,
  Video,
  X,
} from "lucide-react-native"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  Animated,
  Modal,
  Platform,
  ScrollView,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const filterLabels = ["All", "Active", "Completed", "Shared"]
const taskCoachRecommendations = getCoachRecommendations("tasks")

const visibilityOptions: { value: Visibility; label: string; icon: typeof Lock }[] = [
  { value: "private", label: "Private", icon: Lock },
  { value: "group", label: "Growth Circle", icon: UsersRound },
  { value: "public", label: "Public", icon: Globe2 },
]

function getSuggestedSkillIds(query: string) {
  const normalized = query.toLowerCase()
  const keywordMatches = [
    { keywords: ["conflict", "difficult", "repair", "client"], skillIds: ["conflict_management"] },
    { keywords: ["request", "ask", "scope", "task"], skillIds: ["clear_communication_requests"] },
    { keywords: ["stress", "reset", "focus", "deep"], skillIds: ["stress_tolerance_recovery"] },
    {
      keywords: ["listen", "feedback", "team"],
      skillIds: ["active_listening_presence", "empathy_perspective_taking"],
    },
    { keywords: ["negotiate", "contract", "influence"], skillIds: ["influence_persuasion"] },
  ]

  const matched = keywordMatches.flatMap((match) =>
    match.keywords.some((keyword) => normalized.includes(keyword)) ? match.skillIds : [],
  )

  return Array.from(new Set(matched.length > 0 ? matched : allSkills.slice(0, 4).map((s) => s.id)))
}

export default function TasksScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const { coachActionId, goalLinked, taskId, taskPersisted, taskMessage } = useLocalSearchParams<{
    coachActionId?: string
    goalLinked?: string
    taskId?: string
    taskPersisted?: string
    taskMessage?: string
  }>()
  const [taskItems, setTaskItems] = useState<GrowthTask[]>(() => {
    if (isSignedIn) return []
    if (!coachActionId) return tasks

    const action = getCoachAction(coachActionId)
    const coachTask = createTaskFromCoachAction(action)

    return [
      {
        ...coachTask,
        id: taskPersisted === "1" && taskId ? taskId : coachTask.id,
        goal: goalLinked === "0" ? "Coach Mode practice" : coachTask.goal,
      },
      ...tasks.filter((task) => task.id !== coachTask.id),
    ]
  })
  const [draftTitle, setDraftTitle] = useState(
    isSignedIn ? "" : "Resolve feedback tension with the design team",
  )
  const [goalItems, setGoalItems] = useState<ActiveGoal[]>(isSignedIn ? [] : activeGoals)
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(
    isSignedIn ? null : (activeGoals[0]?.id ?? null),
  )
  const [draftGoalTitle, setDraftGoalTitle] = useState(
    isSignedIn ? "" : "Build a cleaner feedback loop",
  )
  const [draftGoalContext, setDraftGoalContext] = useState(
    isSignedIn ? "" : "Practice active listening and close repair gaps in real conversations.",
  )
  const [selectedDraftSkills, setSelectedDraftSkills] = useState<string[]>(
    isSignedIn ? [] : ["active_listening_presence", "conflict_management"],
  )
  const [activeFilter, setActiveFilter] = useState("All")
  const [completionTask, setCompletionTask] = useState<GrowthTask | null>(null)
  const [reflectionText, setReflectionText] = useState("")
  const [includeVideo, setIncludeVideo] = useState(false)
  const [visibility, setVisibility] = useState<Visibility>("private")
  const [draftSaving, setDraftSaving] = useState(false)
  const [goalSaving, setGoalSaving] = useState(false)
  const [taskSyncMessage, setTaskSyncMessage] = useState(
    typeof taskMessage === "string" && taskMessage
      ? taskMessage
      : isSignedIn
        ? "Loading your Supabase tasks."
        : "Using local fallback tasks until Supabase task sync is available.",
  )
  const [goalSyncMessage, setGoalSyncMessage] = useState(
    isSignedIn
      ? "Loading your Supabase goals."
      : "Using local fallback goals until Supabase goal sync is available.",
  )

  const suggestedSkillIds = useMemo(() => getSuggestedSkillIds(draftTitle), [draftTitle])
  const visibleTaskItems = isSignedIn
    ? taskItems.filter((task) => isSupabaseUuid(task.id))
    : taskItems
  const visibleGoalItems = isSignedIn
    ? goalItems.filter((goal) => isSupabaseUuid(goal.id))
    : goalItems
  const dailySummary = getTodayGrowthSummary(visibleTaskItems)
  const scrollY = useRef(new Animated.Value(0)).current
  const filteredTasks = visibleTaskItems.filter((task) => {
    if (activeFilter === "Active") return task.status !== "Completed"
    if (activeFilter === "Completed") return task.status === "Completed"
    if (activeFilter === "Shared") return task.reflection?.visibility === "group"
    return true
  })
  const primaryTaskId = filteredTasks.find((task) => task.status !== "Completed")?.id

  function toggleDraftSkill(skillId: string) {
    setSelectedDraftSkills((current) =>
      current.includes(skillId)
        ? current.filter((currentSkillId) => currentSkillId !== skillId)
        : [...current, skillId],
    )
  }

  function openCompletion(task: GrowthTask) {
    setCompletionTask(task)
    setReflectionText(task.reflection?.text ?? "")
    setVisibility(task.reflection?.visibility ?? "private")
    setIncludeVideo(Boolean(task.reflection?.videoUrl))
  }

  async function createDraftGoal() {
    const title = draftGoalTitle.trim()
    if (!title || goalSaving) return

    const skillIds = selectedDraftSkills.length > 0 ? selectedDraftSkills : suggestedSkillIds

    setGoalSaving(true)
    const result = await createEqGoal({
      title,
      context: draftGoalContext,
      skillIds,
      visibility: "private",
    })
    if (!isSignedIn || result.persisted) {
      const nextGoal: ActiveGoal = {
        id: result.persisted && result.id ? result.id : `local-goal-${Date.now()}`,
        title,
        context: draftGoalContext.trim() || "Created from the Tasks goal planner.",
        due: "Soon",
        skillIds,
      }

      setGoalItems((current) => [nextGoal, ...current.filter((goal) => goal.id !== nextGoal.id)])
      setSelectedGoalId(nextGoal.id)
    }
    setGoalSyncMessage(result.message)
    setGoalSaving(false)
  }

  async function createDraftTask() {
    const title = draftTitle.trim()
    if (!title || draftSaving) return

    const skillIds = selectedDraftSkills.length > 0 ? selectedDraftSkills : suggestedSkillIds
    const primarySkillId = skillIds[0] ?? "active_listening_presence"
    const skill = getSkill(primarySkillId)
    const selectedGoal = visibleGoalItems.find((goal) => goal.id === selectedGoalId)

    setDraftSaving(true)
    const result = await createEqTask({
      skillId: primarySkillId,
      skillIds,
      level: `Level ${Math.min(skill.completedLevels + 1, skill.levels.length)}`,
      title,
      reflection: "Created from the Tasks composer.",
      goalId: selectedGoalId,
      visibility: "private",
    })

    if (!isSignedIn || result.persisted) {
      const nextTask: GrowthTask = {
        id: result.persisted && result.id ? result.id : `local-task-${Date.now()}`,
        title,
        goal: selectedGoal?.title ?? "Coach Mode practice",
        domainId: skill.domainId,
        skillIds,
        suggestedSkillIds: skillIds.filter((skillId) => skillId !== primarySkillId),
        checkpoint: `Level ${Math.min(skill.completedLevels + 1, skill.levels.length)}`,
        due: "Soon",
        status: "Active",
        nextStep: "Complete this task, then add a short reflection while the details are fresh.",
      }

      setTaskItems((current) => [nextTask, ...current.filter((task) => task.id !== nextTask.id)])
    }
    setTaskSyncMessage(result.message)
    setDraftSaving(false)
  }

  useEffect(() => {
    let cancelled = false

    async function loadBackendPlanningData() {
      const [taskResult, goalResult] = await Promise.all([listEqTasks(), listEqGoals()])
      if (cancelled) return

      if (taskResult.mode === "supabase" || isSignedIn) {
        setTaskItems(taskResult.items)
      }
      if (goalResult.mode === "supabase" || isSignedIn) {
        setGoalItems(goalResult.items)
        setSelectedGoalId((current) =>
          current && goalResult.items.some((goal) => goal.id === current)
            ? current
            : (goalResult.items[0]?.id ?? null),
        )
      }
      setTaskSyncMessage(
        typeof taskMessage === "string" && taskMessage
          ? `${taskMessage} ${taskResult.message}`
          : taskResult.message,
      )
      setGoalSyncMessage(goalResult.message)
    }

    loadBackendPlanningData()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, isSignedIn, taskMessage])

  async function completeTask(mode: "simple" | "reflection" | "video") {
    if (!completionTask) return

    const completedDate = getLocalDateKey()
    const shouldAttachReflection = mode !== "simple" || reflectionText.trim().length > 0
    const nextTasks = taskItems.map((task) =>
      task.id === completionTask.id
        ? {
            ...task,
            status: "Completed" as const,
            completedAt: completedDate,
            reflection: shouldAttachReflection
              ? {
                  text:
                    reflectionText.trim() ||
                    "I noticed the skill in a real task and captured the learning while it was fresh.",
                  videoUrl:
                    mode === "video" || includeVideo ? `local://video/${task.id}` : undefined,
                  date: completedDate,
                  visibility,
                }
              : task.reflection,
          }
        : task,
    )

    const reflectionValue =
      reflectionText.trim() ||
      (shouldAttachReflection
        ? "I noticed the skill in a real task and captured the learning while it was fresh."
        : "")
    const completionResult = await completeEqTask({
      taskId: completionTask.id,
      reflectionText: reflectionValue,
      visibility,
      completedAt: new Date().toISOString(),
    })
    if (!isSignedIn || completionResult.persisted) {
      setTaskItems(nextTasks)
    }

    if (shouldAttachReflection) {
      const journalResult = await createEqJournalEntry({
        title: completionTask.title,
        reflectionText: reflectionValue,
        skillIds: completionTask.skillIds,
        taskId: completionResult.persisted ? completionTask.id : null,
        contactId: completionTask.contactId,
        visibility,
      })
      let nextMessage = `${completionResult.message} ${journalResult.message}`
      if (completionTask.contactId) {
        const interactionResult = await recordEqContactInteraction({
          contactId: completionTask.contactId,
          taskId: completionResult.persisted ? completionTask.id : null,
          journalEntryId: journalResult.persisted ? journalResult.id : null,
          title: completionTask.title,
          notes: reflectionValue,
          mood: "positive",
          skillScores: completionTask.skillIds.map((skillId) => ({
            skillId,
            score: 75,
            checkpointLevel: completionTask.checkpoint,
            label: completionTask.title,
          })),
        })
        nextMessage = `${nextMessage} ${interactionResult.message}`
      }
      setTaskSyncMessage(nextMessage)
    } else {
      let nextMessage = completionResult.message
      if (completionTask.contactId) {
        const interactionResult = await recordEqContactInteraction({
          contactId: completionTask.contactId,
          taskId: completionResult.persisted ? completionTask.id : null,
          title: completionTask.title,
          notes: "Completed a contact-linked coaching task.",
          mood: "positive",
          skillScores: completionTask.skillIds.map((skillId) => ({
            skillId,
            score: 70,
            checkpointLevel: completionTask.checkpoint,
            label: completionTask.title,
          })),
        })
        nextMessage = `${nextMessage} ${interactionResult.message}`
      }
      setTaskSyncMessage(nextMessage)
    }

    setCompletionTask(null)
    setReflectionText("")
    setIncludeVideo(false)
    setVisibility("private")
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="EMO.coach Actions"
        title={screenCopy.tasks.title}
        subtitle={screenCopy.tasks.subtitle}
        accentColor={Colors.dark.growth}
        statusText={taskSyncMessage}
        rightAction={
          <TouchableOpacity
            className="h-11 w-11 items-center justify-center rounded-full"
            style={{ backgroundColor: Colors.dark.surfaceSecondary }}
          >
            <Filter color={Colors.dark.growth} size={20} />
          </TouchableOpacity>
        }
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 132 : 116,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <DailyGrowthSummary summary={dailySummary} />

        <TasksCoachCard recommendation={taskCoachRecommendations[0]} />

        <GoalPlanningPanel
          goals={visibleGoalItems}
          selectedGoalId={selectedGoalId}
          draftTitle={draftGoalTitle}
          draftContext={draftGoalContext}
          skillIds={selectedDraftSkills.length > 0 ? selectedDraftSkills : suggestedSkillIds}
          message={goalSyncMessage}
          saving={goalSaving}
          onSelectGoal={setSelectedGoalId}
          onChangeTitle={setDraftGoalTitle}
          onChangeContext={setDraftGoalContext}
          onCreate={createDraftGoal}
        />

        <DraftTaskComposer
          draftTitle={draftTitle}
          selectedGoal={visibleGoalItems.find((goal) => goal.id === selectedGoalId)}
          selectedSkillIds={selectedDraftSkills}
          suggestedSkillIds={suggestedSkillIds}
          saving={draftSaving}
          onChangeTitle={setDraftTitle}
          onToggleSkill={toggleDraftSkill}
          onCreate={createDraftTask}
        />

        <ScrollView horizontal showsHorizontalScrollIndicator={false} className="-mx-5 mb-5 px-5">
          <HStack className="items-center gap-2">
            {filterLabels.map((filter) => (
              <TouchableOpacity
                key={filter}
                activeOpacity={0.86}
                onPress={() => setActiveFilter(filter)}
              >
                <Box
                  className="rounded-full border px-4 py-2"
                  style={{
                    ...filterChipStyle,
                    backgroundColor:
                      filter === activeFilter
                        ? `${Colors.dark.growth}29`
                        : Colors.dark.surfaceSecondary,
                    borderColor:
                      filter === activeFilter ? Colors.dark.growth : "rgba(255,255,255,0.06)",
                  }}
                >
                  <Text
                    className="text-sm font-bold"
                    style={{
                      color:
                        filter === activeFilter ? Colors.dark.growth : Colors.dark.textSecondary,
                    }}
                  >
                    {filter}
                  </Text>
                </Box>
              </TouchableOpacity>
            ))}
          </HStack>
        </ScrollView>

        {filteredTasks.length > 0 ? (
          <VStack className="gap-4">
            {filteredTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                primaryAction={task.id === primaryTaskId}
                onComplete={() => openCompletion(task)}
              />
            ))}
          </VStack>
        ) : (
          <EmptyState
            icon={ListTodo}
            title="No tasks yet"
            description="Create a coaching action from a skill so daily practice moves your EMO.coach levels."
          />
        )}
      </Animated.ScrollView>

      <CompletionModal
        task={completionTask}
        reflectionText={reflectionText}
        includeVideo={includeVideo}
        visibility={visibility}
        onChangeReflection={setReflectionText}
        onToggleVideo={() => setIncludeVideo((current) => !current)}
        onChangeVisibility={setVisibility}
        onClose={() => setCompletionTask(null)}
        onComplete={completeTask}
      />
    </SafeAreaView>
  )
}

function TasksCoachCard({ recommendation }: { recommendation: CoachRecommendation | undefined }) {
  if (!recommendation) return null

  return (
    <Box
      className="mb-5 rounded-3xl border p-4"
      style={{
        backgroundColor: `${Colors.dark.growth}1A`,
        borderColor: `${Colors.dark.growth}4D`,
      }}
    >
      <HStack className="items-start gap-3">
        <Box
          className="h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.growth}2E` }}
        >
          <TrendingUp color={Colors.dark.growth} size={18} />
        </Box>
        <VStack className="flex-1 gap-2">
          <VStack className="gap-1">
            <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
              CONTEXTUAL COACH
            </Text>
            <Text className="font-bold text-white">{recommendation.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {recommendation.reason}
            </Text>
          </VStack>
          <TouchableOpacity
            activeOpacity={0.86}
            className="self-start rounded-full px-4 py-2"
            style={{
              backgroundColor: "transparent",
              borderColor: `${Colors.dark.growth}6B`,
              borderWidth: 1,
            }}
            onPress={() => router.push(recommendation.route as never)}
          >
            <Text className="text-sm font-bold" style={{ color: Colors.dark.growth }}>
              {recommendation.actionLabel}
            </Text>
          </TouchableOpacity>
        </VStack>
      </HStack>
    </Box>
  )
}

function DailyGrowthSummary({ summary }: { summary: ReturnType<typeof getTodayGrowthSummary> }) {
  const focusSkillId = summary.skills[0]?.id ?? "intrinsic_motivation_follow_through"

  return (
    <Box
      className="mb-6 overflow-hidden rounded-3xl border p-5"
      style={{
        backgroundColor:
          summary.completedCount > 0 ? `${Colors.dark.growth}1F` : `${Colors.dark.growth}14`,
        borderColor:
          summary.completedCount > 0 ? `${Colors.dark.growth}57` : `${Colors.dark.growth}3D`,
        shadowColor: Colors.dark.growth,
        shadowOpacity: summary.completedCount > 0 ? 0.18 : 0.08,
        shadowRadius: 18,
        shadowOffset: { width: 0, height: 10 },
      }}
    >
      <HStack className="items-start gap-3">
        <Box
          className="h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.growth}24` }}
        >
          <TrendingUp color={Colors.dark.growth} size={19} />
        </Box>
        <VStack className="flex-1 gap-3">
          <VStack className="gap-1">
            <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
              DAILY GROWTH SUMMARY
            </Text>
            <Text className="text-lg font-bold text-white">
              {summary.completedCount > 0
                ? `Today you coached ${summary.skills.length} skills`
                : "Complete one task to unlock your daily summary"}
            </Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {summary.completedCount > 0
                ? summary.nextStep
                : "Complete one linked task to see practiced skills and next moves."}
            </Text>
          </VStack>
          <HStack className="flex-wrap items-center gap-2">
            {summary.skills.length > 0 ? (
              summary.skills.map((skill) => <SkillChip key={skill.id} skillId={skill.id} compact />)
            ) : (
              <SkillChip skillId="intrinsic_motivation_follow_through" selected={false} compact />
            )}
          </HStack>
          <VStack className="gap-2">
            <TouchableOpacity
              activeOpacity={0.86}
              onPress={() =>
                router.push({
                  pathname: "/coach/[skillId]",
                  params: { skillId: focusSkillId },
                })
              }
            >
              <Box
                className="rounded-2xl border px-3 py-3"
                style={{
                  backgroundColor: Colors.dark.primary,
                  borderColor: "rgba(255,255,255,0.2)",
                }}
              >
                <HStack className="items-center justify-center gap-2">
                  <Sparkles color={Colors.dark.primaryForeground} size={16} />
                  <Text
                    className="text-sm font-bold"
                    style={{ color: Colors.dark.primaryForeground }}
                  >
                    Coach this moment
                  </Text>
                </HStack>
              </Box>
            </TouchableOpacity>
            <HStack className="gap-2">
              <TouchableOpacity
                activeOpacity={0.86}
                className="flex-1"
                onPress={() => router.push("/journal")}
              >
                <Box
                  className="rounded-2xl border px-3 py-3"
                  style={{
                    backgroundColor: "transparent",
                    borderColor: `${Colors.dark.proof}57`,
                  }}
                >
                  <HStack className="items-center justify-center gap-2">
                    <BookOpenText color={Colors.dark.proof} size={16} />
                    <Text className="text-sm font-bold" style={{ color: Colors.dark.proof }}>
                      Journal
                    </Text>
                  </HStack>
                </Box>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.86}
                className="flex-1"
                onPress={() =>
                  router.push({
                    pathname: "/proof/new",
                    params: {
                      skillId: focusSkillId,
                      level: "Level 3",
                    },
                  })
                }
              >
                <Box
                  className="rounded-2xl border px-3 py-3"
                  style={{
                    backgroundColor: "transparent",
                    borderColor: `${Colors.dark.proof}57`,
                  }}
                >
                  <HStack className="items-center justify-center gap-2">
                    <Video color={Colors.dark.proof} size={16} />
                    <Text className="text-sm font-bold" style={{ color: Colors.dark.proof }}>
                      Log Proof
                    </Text>
                  </HStack>
                </Box>
              </TouchableOpacity>
            </HStack>
          </VStack>
        </VStack>
      </HStack>
    </Box>
  )
}

function DraftTaskComposer({
  draftTitle,
  selectedGoal,
  selectedSkillIds,
  suggestedSkillIds,
  saving,
  onChangeTitle,
  onToggleSkill,
  onCreate,
}: {
  draftTitle: string
  selectedGoal: ActiveGoal | undefined
  selectedSkillIds: string[]
  suggestedSkillIds: string[]
  saving: boolean
  onChangeTitle: (title: string) => void
  onToggleSkill: (skillId: string) => void
  onCreate: () => void | Promise<void>
}) {
  const visibleSkillIds = Array.from(new Set([...selectedSkillIds, ...suggestedSkillIds]))

  return (
    <Box
      className="mb-5 rounded-3xl border p-4"
      style={{
        backgroundColor: Colors.dark.surfaceSecondary,
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <HStack className="mb-3 items-start gap-3">
        <Box
          className="h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.growth}29` }}
        >
          <Flag color={Colors.dark.growth} size={18} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
            SKILL SUGGESTIONS
          </Text>
          <Text className="font-bold text-white">Plan a real task, then attach EQ skills</Text>
          <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
            Goal link: {selectedGoal?.title ?? "No goal selected"}
          </Text>
        </VStack>
      </HStack>

      <TextInput
        value={draftTitle}
        onChangeText={onChangeTitle}
        placeholder="Describe a goal or task"
        placeholderTextColor={Colors.dark.textSecondary}
        style={{
          minHeight: 48,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.08)",
          backgroundColor: Colors.dark.surface,
          color: "white",
          paddingHorizontal: 14,
          paddingVertical: 12,
          fontWeight: "700",
        }}
      />

      <Text className="mb-3 mt-5 text-xs font-bold" style={{ color: Colors.dark.textSecondary }}>
        Tap to accept or remove suggested tags
      </Text>
      <HStack className="flex-wrap items-center gap-2">
        {visibleSkillIds.map((skillId) => (
          <TouchableOpacity
            key={skillId}
            activeOpacity={0.86}
            onPress={() => onToggleSkill(skillId)}
          >
            <SkillChip skillId={skillId} selected={selectedSkillIds.includes(skillId)} compact />
          </TouchableOpacity>
        ))}
      </HStack>

      <Button
        size="lg"
        className="mt-5 rounded-2xl"
        disabled={saving || !draftTitle.trim()}
        style={{ backgroundColor: Colors.dark.growth, opacity: saving ? 0.72 : 1 }}
        onPress={onCreate}
      >
        <Flag color={Colors.dark.primaryForeground} size={16} />
        <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
          {saving ? "Saving..." : "Save Task"}
        </ButtonText>
      </Button>
    </Box>
  )
}

function GoalPlanningPanel({
  goals,
  selectedGoalId,
  draftTitle,
  draftContext,
  skillIds,
  message,
  saving,
  onSelectGoal,
  onChangeTitle,
  onChangeContext,
  onCreate,
}: {
  goals: ActiveGoal[]
  selectedGoalId: string | null
  draftTitle: string
  draftContext: string
  skillIds: string[]
  message: string
  saving: boolean
  onSelectGoal: (goalId: string | null) => void
  onChangeTitle: (title: string) => void
  onChangeContext: (context: string) => void
  onCreate: () => void | Promise<void>
}) {
  const visibleGoals = goals.slice(0, 5)

  return (
    <Box
      className="mb-5 rounded-3xl border p-4"
      style={{
        backgroundColor: `${Colors.dark.growth}12`,
        borderColor: `${Colors.dark.growth}3D`,
      }}
    >
      <HStack className="mb-3 items-start gap-3">
        <Box
          className="h-10 w-10 items-center justify-center rounded-2xl"
          style={{ backgroundColor: `${Colors.dark.growth}29` }}
        >
          <Flag color={Colors.dark.growth} size={18} />
        </Box>
        <VStack className="flex-1 gap-1">
          <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
            GOALS
          </Text>
          <Text className="font-bold text-white">Save goals, then link tasks to the outcome</Text>
          <Text className="text-xs font-bold" style={{ color: Colors.dark.growth }}>
            {message}
          </Text>
        </VStack>
      </HStack>

      <VStack className="gap-3">
        <TextInput
          value={draftTitle}
          onChangeText={onChangeTitle}
          placeholder="Name the goal"
          placeholderTextColor={Colors.dark.textSecondary}
          style={{
            minHeight: 48,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.08)",
            backgroundColor: Colors.dark.surface,
            color: "white",
            paddingHorizontal: 14,
            paddingVertical: 12,
            fontWeight: "700",
          }}
        />
        <TextInput
          value={draftContext}
          onChangeText={onChangeContext}
          placeholder="Why this goal matters"
          placeholderTextColor={Colors.dark.textSecondary}
          multiline
          style={{
            minHeight: 74,
            borderRadius: 16,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.08)",
            backgroundColor: Colors.dark.surface,
            color: "white",
            paddingHorizontal: 14,
            paddingVertical: 12,
            fontWeight: "600",
            textAlignVertical: "top",
          }}
        />

        <HStack className="flex-wrap items-center gap-2">
          {skillIds.slice(0, 4).map((skillId) => (
            <SkillChip key={skillId} skillId={skillId} compact />
          ))}
        </HStack>

        <Button
          size="lg"
          className="rounded-2xl"
          disabled={saving || !draftTitle.trim()}
          style={{ backgroundColor: Colors.dark.growth, opacity: saving ? 0.72 : 1 }}
          onPress={onCreate}
        >
          <Flag color={Colors.dark.primaryForeground} size={16} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
            {saving ? "Saving..." : "Save Goal"}
          </ButtonText>
        </Button>
      </VStack>

      <HStack className="mt-4 flex-wrap items-center gap-2">
        <TouchableOpacity activeOpacity={0.86} onPress={() => onSelectGoal(null)}>
          <Box
            className="rounded-full border px-3 py-2"
            style={{
              ...compactFilterChipStyle,
              backgroundColor: selectedGoalId ? Colors.dark.surface : `${Colors.dark.growth}29`,
              borderColor: selectedGoalId ? "rgba(255,255,255,0.08)" : Colors.dark.growth,
            }}
          >
            <Text
              className="text-xs font-bold"
              style={{ color: selectedGoalId ? Colors.dark.textSecondary : Colors.dark.growth }}
            >
              No goal link
            </Text>
          </Box>
        </TouchableOpacity>
        {visibleGoals.map((goal) => {
          const active = goal.id === selectedGoalId
          return (
            <TouchableOpacity
              key={goal.id}
              activeOpacity={0.86}
              onPress={() => onSelectGoal(goal.id)}
            >
              <Box
                className="rounded-full border px-3 py-2"
                style={{
                  ...compactFilterChipStyle,
                  backgroundColor: active ? `${Colors.dark.growth}29` : Colors.dark.surface,
                  borderColor: active ? Colors.dark.growth : "rgba(255,255,255,0.08)",
                }}
              >
                <Text
                  className="text-xs font-bold"
                  style={{ color: active ? Colors.dark.growth : Colors.dark.textSecondary }}
                  numberOfLines={1}
                >
                  {goal.title}
                </Text>
              </Box>
            </TouchableOpacity>
          )
        })}
      </HStack>
    </Box>
  )
}

function TaskCard({
  task,
  primaryAction,
  onComplete,
}: {
  task: GrowthTask
  primaryAction: boolean
  onComplete: () => void
}) {
  const domain = getDomain(task.domainId)
  const color = domain?.color ?? getSkillDomainColor(task.skillIds[0])
  const linkedSkills = getSkills(task.skillIds)
  const completed = task.status === "Completed"
  const solidAction = primaryAction && !completed

  return (
    <PressableScale>
      <Box
        className="rounded-3xl border p-5"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: `${color}44`,
        }}
      >
        <HStack className="mb-4 items-start justify-between gap-4">
          <VStack className="flex-1 gap-2">
            <Text className="text-lg font-bold text-white">{task.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              Goal: {task.goal}
            </Text>
            <HStack className="items-center gap-2">
              <Link2 color={color} size={15} />
              <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
                {linkedSkills.length} linked skills · {task.checkpoint}
              </Text>
            </HStack>
          </VStack>
          <ChevronRight color={color} size={22} />
        </HStack>

        <VStack className="mb-5 gap-2">
          <HStack className="flex-wrap items-center gap-2">
            {task.skillIds.map((skillId) => (
              <SkillChip key={skillId} skillId={skillId} compact />
            ))}
          </HStack>

          <HStack className="flex-wrap items-center gap-2">
            {task.suggestedSkillIds.map((skillId) => (
              <SkillChip key={skillId} skillId={skillId} selected={false} compact />
            ))}
          </HStack>
        </VStack>

        <Box
          className="mb-4 rounded-2xl border p-3"
          style={{
            backgroundColor: `${color}12`,
            borderColor: `${color}3D`,
          }}
        >
          <HStack className="items-start gap-2">
            <Sparkles color={color} size={16} />
            <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              This task develops{" "}
              {linkedSkills
                .slice(0, 2)
                .map((skill) => skill.title)
                .join(" + ")}
              . After completion, log a proof so the Passport can show real progress evidence.
            </Text>
          </HStack>
        </Box>

        <TaskContactTrend task={task} />

        <HStack className="items-center justify-between gap-3">
          <HStack className="flex-1 items-center gap-2">
            <CalendarClock color={Colors.dark.textSecondary} size={16} />
            <Text className="text-sm" style={{ color: Colors.dark.textSecondary }}>
              {task.due}
            </Text>
          </HStack>
          <Box className="rounded-full px-3 py-1" style={{ backgroundColor: `${color}22` }}>
            <Text className="text-xs font-bold" style={{ color }}>
              {task.status}
            </Text>
          </Box>
        </HStack>

        <HStack className="mt-4 gap-2">
          <TouchableOpacity
            activeOpacity={0.86}
            className="flex-1"
            disabled={completed}
            onPress={onComplete}
          >
            <Box
              className="rounded-2xl border px-3 py-3"
              style={{
                backgroundColor: solidAction ? Colors.dark.growth : "transparent",
                borderColor: solidAction ? "rgba(255,255,255,0.2)" : `${color}66`,
                opacity: completed ? 0.72 : 1,
              }}
            >
              <HStack className="items-center justify-center gap-2">
                <CheckCircle2
                  color={solidAction ? Colors.dark.primaryForeground : color}
                  size={17}
                />
                <Text
                  className="text-sm font-bold"
                  style={{ color: solidAction ? Colors.dark.primaryForeground : color }}
                >
                  {completed ? "Completed" : "Complete"}
                </Text>
              </HStack>
            </Box>
          </TouchableOpacity>
          <TouchableOpacity activeOpacity={0.86} onPress={() => router.push("/journal")}>
            <Box
              className="rounded-2xl border px-4 py-3"
              style={{
                backgroundColor: Colors.dark.surface,
                borderColor: "rgba(255,255,255,0.08)",
              }}
            >
              <BookOpenText color={Colors.dark.proof} size={17} />
            </Box>
          </TouchableOpacity>
        </HStack>
      </Box>
    </PressableScale>
  )
}

function TaskContactTrend({ task }: { task: GrowthTask }) {
  const match = getContactTrendForTask(task)
  if (!match) return null

  return <TaskContactTrendCard contact={match.contact} trend={match.trend} />
}

function TaskContactTrendCard({
  contact,
  trend,
}: {
  contact: NetworkContact
  trend: ContactSkillTrend
}) {
  const skill = getSkill(trend.skillId)
  const trendColor =
    trend.direction === "negative"
      ? Colors.dark.error
      : trend.direction === "positive"
        ? Colors.dark.growth
        : Colors.dark.textSecondary
  const TrendIcon = trend.direction === "negative" ? TrendingDown : TrendingUp

  return (
    <TouchableOpacity
      activeOpacity={0.86}
      onPress={() =>
        router.push({
          pathname: "/contact/[id]",
          params: { id: contact.id },
        })
      }
    >
      <Box
        className="mb-4 rounded-2xl border p-3"
        style={{
          backgroundColor: `${trendColor}12`,
          borderColor: `${trendColor}3D`,
        }}
      >
        <HStack className="items-start gap-2">
          <TrendIcon color={trendColor} size={16} />
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color: trendColor }}>
              CONTACT TREND
            </Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {contact.name} · {skill.title}{" "}
              <Text className="font-bold" style={{ color: trendColor }}>
                {trend.changePercent > 0 ? "+" : ""}
                {trend.changePercent}%
              </Text>
            </Text>
          </VStack>
          <ChevronRight color={trendColor} size={17} />
        </HStack>
      </Box>
    </TouchableOpacity>
  )
}

function CompletionModal({
  task,
  reflectionText,
  includeVideo,
  visibility,
  onChangeReflection,
  onToggleVideo,
  onChangeVisibility,
  onClose,
  onComplete,
}: {
  task: GrowthTask | null
  reflectionText: string
  includeVideo: boolean
  visibility: Visibility
  onChangeReflection: (value: string) => void
  onToggleVideo: () => void
  onChangeVisibility: (value: Visibility) => void
  onClose: () => void
  onComplete: (mode: "simple" | "reflection" | "video") => void
}) {
  if (!task) return null

  const color = Colors.dark.proof

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onClose}>
      <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0, 0, 0, 0.58)" }}>
        <Box
          className="max-h-[90%] rounded-t-[28px] border-t p-6"
          style={{
            backgroundColor: Colors.dark.surface,
            borderColor: `${Colors.dark.proof}3D`,
          }}
        >
          <ScrollView showsVerticalScrollIndicator={false}>
            <HStack className="mb-4 items-start justify-between gap-3">
              <VStack className="flex-1 gap-1">
                <Text className="text-xs font-bold" style={{ color }}>
                  LEVEL PROGRESS CAPTURED
                </Text>
                <Text className="text-3xl font-bold leading-9 text-white">Great work, coach!</Text>
                <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                  You turned a real action into EQ evidence. {task.nextStep}
                </Text>
              </VStack>
              <TouchableOpacity
                className="h-10 w-10 items-center justify-center rounded-full"
                style={{ backgroundColor: Colors.dark.surfaceSecondary }}
                onPress={onClose}
              >
                <X color={Colors.dark.textSecondary} size={20} />
              </TouchableOpacity>
            </HStack>

            <HStack className="mb-6 flex-wrap items-center gap-2">
              {task.skillIds.map((skillId) => (
                <SkillChip key={skillId} skillId={skillId} />
              ))}
            </HStack>

            <Text className="mb-2 font-bold text-white">Quick reflection</Text>
            <Textarea
              size="lg"
              className="mb-4 h-32 rounded-2xl border"
              style={{
                backgroundColor: Colors.dark.surfaceSecondary,
                borderColor: `${color}44`,
              }}
            >
              <TextareaInput
                value={reflectionText}
                onChangeText={onChangeReflection}
                className="p-4 text-white"
                placeholder="What happened, what did you practice, and what changed?"
                placeholderTextColor={Colors.dark.textSecondary}
                multiline
              />
            </Textarea>

            <TouchableOpacity activeOpacity={0.86} onPress={onToggleVideo}>
              <Box
                className="mb-4 rounded-2xl border p-4"
                style={{
                  backgroundColor: includeVideo ? `${color}20` : Colors.dark.surfaceSecondary,
                  borderColor: includeVideo ? `${color}88` : "rgba(255,255,255,0.08)",
                }}
              >
                <HStack className="items-center gap-3">
                  <Video color={color} size={20} />
                  <VStack className="flex-1">
                    <Text className="font-bold text-white">Attach video proof placeholder</Text>
                    <Text className="text-xs" style={{ color: Colors.dark.textSecondary }}>
                      {includeVideo
                        ? "Video proof placeholder selected"
                        : "Disabled until real capture/upload exists"}
                    </Text>
                  </VStack>
                  <Text className="font-bold" style={{ color }}>
                    {includeVideo ? "On" : "Off"}
                  </Text>
                </HStack>
              </Box>
            </TouchableOpacity>

            <Text className="mb-2 font-bold text-white">Visibility</Text>
            <HStack className="mb-5 flex-wrap items-center gap-2">
              {visibilityOptions.map((option) => {
                const Icon = option.icon
                const active = visibility === option.value
                return (
                  <TouchableOpacity
                    key={option.value}
                    activeOpacity={0.86}
                    onPress={() => onChangeVisibility(option.value)}
                  >
                    <Box
                      className="rounded-full border px-3 py-2"
                      style={{
                        ...compactFilterChipStyle,
                        backgroundColor: active ? `${color}29` : Colors.dark.surfaceSecondary,
                        borderColor: active ? color : "rgba(255,255,255,0.08)",
                        opacity: option.value === "public" ? 0.72 : 1,
                      }}
                    >
                      <HStack className="items-center gap-2">
                        <Icon color={active ? color : Colors.dark.textSecondary} size={14} />
                        <Text
                          className="text-xs font-bold"
                          style={{
                            color: active ? color : Colors.dark.textSecondary,
                          }}
                        >
                          {option.label}
                        </Text>
                      </HStack>
                    </Box>
                  </TouchableOpacity>
                )
              })}
            </HStack>

            <VStack className="gap-3 pb-2">
              <Button
                size="xl"
                className="rounded-2xl"
                style={{ backgroundColor: Colors.dark.primary }}
                onPress={() => {
                  onComplete("simple")
                  router.push({
                    pathname: "/coach/[skillId]",
                    params: { skillId: task.skillIds[0] },
                  })
                }}
              >
                <Sparkles color={Colors.dark.primaryForeground} size={18} />
                <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
                  Coach this moment
                </ButtonText>
              </Button>
              <Button
                size="xl"
                className="rounded-2xl"
                variant="outline"
                style={{
                  backgroundColor: `${color}18`,
                  borderColor: `${color}66`,
                }}
                onPress={() => onComplete("reflection")}
              >
                <BookOpenText color={color} size={18} />
                <ButtonText className="font-bold" style={{ color }}>
                  Add Reflection
                </ButtonText>
              </Button>
              <Button
                size="xl"
                className="rounded-2xl"
                variant="outline"
                style={{
                  backgroundColor: `${color}18`,
                  borderColor: `${color}66`,
                }}
                onPress={() => onComplete("video")}
              >
                <Video color={color} size={18} />
                <ButtonText className="font-bold" style={{ color }}>
                  Add Video
                </ButtonText>
              </Button>
              <Button
                size="xl"
                className="rounded-2xl"
                variant="outline"
                style={{
                  backgroundColor: Colors.dark.surfaceSecondary,
                  borderColor: "rgba(255,255,255,0.12)",
                }}
                onPress={() => onComplete("simple")}
              >
                <ButtonText className="font-bold text-white">Skip for now</ButtonText>
              </Button>
            </VStack>
          </ScrollView>
        </Box>
      </View>
    </Modal>
  )
}
